import PropTypes from 'prop-types';
import React from 'react';
import i18n from 'i18next';
import { Helmet } from 'react-helmet-async';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import Layout from './Layout';
import MetaTags from './MetaTags';
import { H1Tag, getRouteData } from './utils';
import { getAlternateLanguageCode, getLocalePaths } from '../utils/getCanonicalLink';

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.
class RouteHandler extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: true,
      defaultLanguage: 'en',
    };
    const routeData = this.extractRouteData();

    // route data from react-router - if route was resolved, it's not a 404
    if (props.route !== null) {
      this.state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (routeData && (!routeData.sitecore || !routeData.sitecore.route)) {
      this.state.notFound = true;
    }

    // if we have an SSR state, and that state has language data, set the current language
    // (this makes the language of content follow the Sitecore context language cookie)
    // note that a route-based language (i.e. /en) will override this default; this is for home.
    if (routeData && routeData.sitecore && routeData.sitecore.context && routeData.sitecore.context.language) {
      this.state.defaultLanguage = routeData.sitecore.context.language;
    }

    // tell i18next to sync its current language with the route language
    this.updateLanguage();
  }

  componentDidMount() {
    const routeData = this.extractRouteData();
    const { language, languageSelectors } = routeData.sitecore.context;

    // if no existing routeData is present (from SSR), get Layout Service fetching the route data or ssr render complete
    if (!routeData || this.props.ssrRenderComplete) {
      this.updateRouteData();
      return;
    }

    if (this.props.updateExternalLanguageLinks) {
      this.props.updateExternalLanguageLinks(language, languageSelectors);
    }
  }

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.route.match.url;
    const newRoute = this.props.route.match.url;
    const isSectionNavRouteUpdate = Boolean(
      this.props.sitecoreContext?.route?.fields?.sectionNavLabel?.value &&
        previousProps.sitecoreContext?.route?.fields?.sectionNavLabel?.value,
    );

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute) {
      return;
    }

    // if in experience editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isEditorActive()) {
      window.location.assign(newRoute);
      return;
    }

    this.updateLanguage();
    this.updateRouteData(!isSectionNavRouteUpdate);
  }

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateRouteData(shouldScrollToTop) {
    let sitecoreRoutePath = this.props.route.match.params.sitecoreRoute || '/';
    if (!sitecoreRoutePath.startsWith('/')) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    const lang = this.props.route.match.params.lang || this.state.defaultLanguage;
    const { config } = this.props;

    // get the route data for the new route
    getRouteData(sitecoreRoutePath, lang, config).then(routeData => {
      // set the sitecore context data and push the new route
      if (routeData?.sitecore?.route) {
        const newContext = {
          route: routeData.sitecore.route,
          itemId: routeData.sitecore.route.itemId,
          provinces: this.props.sitecoreContext.provinces,
          settings: this.props.sitecoreContext.settings,
          sharedApps: this.props.sitecoreContext.sharedApps,
          hondaRestApiHost: this.props.sitecoreContext.hondaRestApiHost,
          sotHondaExperienceApiConfig: this.props.sitecoreContext.sotHondaExperienceApiConfig,
          hondaImageHost: this.props.sitecoreContext.hondaImageHost,
          recallAssetContext: this.props.sitecoreContext.recallAssetContext,
          sitecoreApiKey: this.props.sitecoreContext.sitecoreApiKey,
          sitecoreApiHost: this.props.sitecoreContext.sitecoreApiHost,
          layoutServiceRoute: this.props.sitecoreContext.layoutServiceRoute,
          ...routeData.sitecore.context,
        };
        this.props.updateSitecoreContext(newContext);

        if (this.props.onRouteChange) {
          this.props.onRouteChange(routeData.sitecore.route);
        }

        const { language, languageSelectors } = routeData.sitecore.context;

        if (this.props.updateExternalLanguageLinks) {
          this.props.updateExternalLanguageLinks(language, languageSelectors);
        }

        this.setState({ notFound: false }, () => {
          if (shouldScrollToTop && typeof window !== 'undefined') {
            window.scrollTo(0, 0);
          }
        });
      } else {
        this.setState({ notFound: true }, () => {
          this.props.updateSitecoreContext(routeData.sitecore.context);

          if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
          }
        });
      }
    });
  }

  extractRouteData() {
    if (!this.props.sitecoreContext) return null;

    const { route, ...context } = this.props.sitecoreContext;

    return {
      sitecore: {
        route,
        context,
      },
    };
  }

  /**
   * Updates the current app language to match the route data.
   */
  updateLanguage() {
    const newLanguage = this.props.route.match.params.lang || this.state.defaultLanguage;

    if (i18n.language !== newLanguage) {
      i18n.changeLanguage(newLanguage);
    }
  }

  render() {
    const { notFound } = this.state;
    const routeData = this.extractRouteData();
    const { canonicalHref } = this.props.config;
    const { language } = routeData.sitecore.context;
    const { languageSelectors } = routeData.sitecore.context;

    const canonicalProps = this.props.sitecoreContext?.route?.fields;
    const searchParams = this.props.route?.location?.search;
    const pathName = this.props.route?.location?.pathname;

    const { canonicalPath, alternatePath } = getLocalePaths({
      currentLanguageCode: language,
      languageSelectors,
      canonicalProps,
      searchParams,
      pathName,
    });

    const alternateLanguageCode = getAlternateLanguageCode(language, languageSelectors);

    const h1Value = routeData?.sitecore?.route?.fields?.h1Tag?.value;

    // no route data for the current route in Sitecore - show not found component.
    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and
    // Sitecore integrated rendering know how to send 404 status codes).
    if (notFound && routeData) {
      return this.props.errorMessage;
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!routeData) {
      return null;
    }

    // Render the app's root structural layout
    return (
      <>
        <MetaTags metadata={routeData.sitecore.context?.pageMetaData} />
        {h1Value && <H1Tag>{h1Value}</H1Tag>}
        <Helmet>
          <link rel="canonical" href={`${canonicalHref}${canonicalPath}`} />
          {alternateLanguageCode && (
            <link rel="alternate" hrefLang={`${alternateLanguageCode}-ca`} href={`${canonicalHref}${alternatePath}`} />
          )}
          <link rel="alternate" hrefLang={`${language}-ca`} href={`${canonicalHref}${canonicalPath}`} />
        </Helmet>
        <Layout placeholderName={this.props.placeholderName} route={routeData.sitecore.route} />
      </>
    );
  }
}

RouteHandler.propTypes = {
  route: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    match: PropTypes.shape({
      url: PropTypes.string.isRequired,
      params: PropTypes.shape({
        lang: PropTypes.string,
        sitecoreRoute: PropTypes.string,
      }),
    }),
  }),
  ssrRenderComplete: PropTypes.bool.isRequired,

  updateExternalLanguageLinks: PropTypes.func,
  updateSitecoreContext: PropTypes.func.isRequired,
  config: PropTypes.shape({
    sitecoreApiHost: PropTypes.string.isRequired,
    sitecoreApiKey: PropTypes.string.isRequired,
    layoutServiceRoute: PropTypes.string.isRequired,
    canonicalHref: PropTypes.string,
  }).isRequired,
  onRouteChange: PropTypes.func,
  language: PropTypes.string,
  errorMessage: PropTypes.string,
  placeholderName: PropTypes.string.isRequired,
  sitecoreContext: PropTypes.shape({
    route: PropTypes.shape({
      fields: PropTypes.shape({
        canonicalUrl: PropTypes.shape({
          value: PropTypes.string,
        }),
        canonicalParams: PropTypes.shape({
          value: PropTypes.string,
        }),
        sectionNavLabel: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    }),
    provinces: PropTypes.arrayOf(PropTypes.shape({})),
    settings: PropTypes.shape({}),
    sharedApps: PropTypes.arrayOf(PropTypes.shape({})),
    hondaRestApiHost: PropTypes.string,
    sotHondaExperienceApiConfig: PropTypes.shape({
      consumerName: PropTypes.string,
      consumerId: PropTypes.string,
      env: PropTypes.string,
    }),
    hondaImageHost: PropTypes.string,
    recallAssetContext: PropTypes.string,
    sitecoreApiKey: PropTypes.string,
    sitecoreApiHost: PropTypes.string,
    layoutServiceRoute: PropTypes.string,
  }),
};

export default withSitecoreContext({ updatable: true })(RouteHandler);
